/* ================================================================
   AISTROWISE — Calculadora de carta natal (deferred, no libraries)

   SECURITY (non-negotiable):
   - Calls the PUBLIC endpoint POST api.aistrowise.com/natal-positions.
   - Sends NO credentials: no X-API-Key, no cookies (credentials:'omit').
   - date + time are mandatory (no unknown-hour path); validated client-side.
   - Birth data is sent ONLY to api.aistrowise.com. It is never stored
     (no localStorage), never logged (no console), and never attached to
     the calculator_complete analytics event.
   - Opened from the guide (#g=p.guia) it READS the guide's encrypted
     same-origin store (aw_guia_v2, via AWGuiaStore) for an exact-time
     birth, and writes nothing.
   - «Comparte tu carta» (aw-share.js) gets the response and the payload by
     direct call. Shared links carry no birth data. Only «Abrir en Chrome»
     (Android in-app browsers) hands HER birth data to HER Chrome after the #,
     never to a server, and aw-prefill scrubs it on arrival.
   ================================================================ */
(function () {
  'use strict';

  // Plan inglés (2026-10-20): T(texto) devuelve ese texto en el idioma de la visita (window.awT, layout/theme.liquid).
  // Los nombres que manda la API en español pasan por T() solo donde se pintan, nunca en la lógica.
  var T = (typeof window !== 'undefined' && window.awT) || function (s) { return s; };
  function isEn() { return typeof window !== 'undefined' && window.awLang === 'en'; }
  // Plan francés (2026-10-21): el francés toma el formulario español (día, mes, año y 24 horas) y escribe la fecha y la
  // hora a la francesa en la prosa del resultado. En español y en inglés nada cambia.
  function isFr() { return typeof window !== 'undefined' && window.awLang === 'fr'; }

  var SIGN_GLYPHS = {
    aries: '♈', tauro: '♉', geminis: '♊', cancer: '♋',
    leo: '♌', virgo: '♍', libra: '♎', escorpio: '♏',
    sagitario: '♐', capricornio: '♑', acuario: '♒', piscis: '♓'
  };
  var PERSONAL = ['sol', 'luna', 'mercurio', 'venus', 'marte'];
  var SOCIAL = ['jupiter', 'saturno', 'urano', 'neptuno', 'pluton'];
  var POINT_ORDER = { 'nodo norte': 1, 'nodo sur': 2, 'quiron': 3, 'lilith': 4 };
  var RELATIONAL = ['venus', 'marte', 'luna'];

  function norm(s) {
    return String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  }
  // The line of one sign from a "Sign | text" list, one sign per line (focus_mc_signs of the midheaven page, 2026-10-29).
  // names: the sign as the API gives it and as the visitor reads it (G\u00e9minis, Gemini).
  function signLine(list, names) {
    var want = (names || []).map(norm), out = '';
    String(list || '').split('\n').forEach(function (l) {
      var i = l.indexOf('|');
      if (i > 0 && want.indexOf(norm(l.slice(0, i))) >= 0) out = l.slice(i + 1).trim();
    });
    return out;
  }
  function pad2(n) { return String(n).length < 2 ? '0' + n : String(n); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  // Force monochrome TEXT presentation of astrological glyphs (signs, planets,
  // nodes, Quiron, Lilith) by appending U+FE0E after each symbol char, exactly
  // as the design does (the mock renders glyphs as "glyph + &#xFE0E;"). Without
  // it, iOS/Android render them as colour emoji. Harmless on symbols with no
  // emoji variant. Ranges: arrows (U+2190-U+21FF) + misc symbols (U+2600-U+27BF).
  function forceText(s) {
    return String(s == null ? '' : s).replace(/([←-⇿☀-➿])/g, '$1︎');
  }
  function time12(hhmm) {
    var m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || ''));
    if (!m) return hhmm;
    var h = +m[1];
    return (h % 12 || 12) + ':' + m[2] + (h < 12 ? ' am' : ' pm');
  }
  // «14 h 30» en la prosa francesa (espacios sin corte; en los campos del formulario sigue siendo 14:30).
  function timeFr(hhmm) {
    var m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || ''));
    return m ? +m[1] + '\u00a0h\u00a0' + m[2] : hhmm;
  }
  // «14 mars 2027», «1er mars 2027»: el día sin cero delante y el 1 como ordinal, el mes en minúscula (el del selector).
  function dateFr(day, monthName, year) {
    var d = parseInt(day, 10);
    return (d === 1 ? '1er' : String(d)) + ' ' + monthName + ' ' + year;
  }
  function fmt(tpl, map) {
    return String(tpl || '').replace(/\{(\w+)\}/g, function (m, k) { return map[k] != null ? map[k] : ''; });
  }
  // Cierre de los aspectos (founder, 25-sep): «Aquí ves 3 de tus N.» solo cuando la carta
  // tiene más aspectos de los que se enseñan, y detrás la frase del ajuste aspect_close.
  function aspectCloseText(t, shown, total) {
    var lead = total > shown && t.aspectShownTpl ? fmt(T(t.aspectShownTpl), { shown: shown, n: total }) : '';
    return [lead, t.aspectClose || ''].filter(Boolean).join(' ');
  }
  function bucket(nameEs) {
    var n = norm(nameEs);
    if (PERSONAL.indexOf(n) > -1) return 'personal';
    if (SOCIAL.indexOf(n) > -1) return 'social';
    return 'points';
  }
  function pointRank(nameEs) {
    var n = norm(nameEs);
    var keys = Object.keys(POINT_ORDER);
    for (var i = 0; i < keys.length; i++) { if (n.indexOf(keys[i]) > -1) return POINT_ORDER[keys[i]]; }
    return 9;
  }

  // Editorial re-rank of the visible aspect chips (spec 2026-07-29): up to TWO
  // aspects involving Venus, Marte or Luna are promoted first (tightest orb
  // first); everything else keeps API order, so the chart's own leading aspect
  // always keeps a chip slot. Selection only — every chip stays a real
  // intra-chart aspect and is presented exactly as before.
  function rankAspectsRelational(aspects) {
    var list = aspects || [];
    var orbOf = function (a) { return typeof a.orb === 'number' ? a.orb : 999; };
    var rel = list.filter(function (a) {
      return RELATIONAL.indexOf(norm(a.p1_es)) > -1 || RELATIONAL.indexOf(norm(a.p2_es)) > -1;
    });
    rel.sort(function (a, b) { return orbOf(a) - orbOf(b); });
    var promoted = rel.slice(0, 2);
    var rest = list.filter(function (a) { return promoted.indexOf(a) === -1; });
    return promoted.concat(rest);
  }

  // Dignity end-beat selection (spec 2026-07-30): the chart's most notable
  // essential dignity, luminaries and personal planets first, then the social
  // pair that only the Profundizador interprets. Classical dignities only —
  // peregrino and domicilio moderno never render, outers never enter the
  // ladder. Returns null when nothing qualifies (silent suppression, tested).
  var DIG_PLANETS = ['sol', 'luna', 'mercurio', 'venus', 'marte', 'saturno', 'jupiter'];
  var DIG_CLASSES = ['domicilio', 'exaltacion', 'detrimento', 'caida'];
  function selectDignity(data) {
    if (!data || !data.dignities || !data.planets) return null;
    var signOf = {};
    (data.planets || []).forEach(function (p) { if (p && p.name_es && p.sign_es) signOf[norm(p.name_es)] = p.sign_es; });
    var digOf = {};
    try {
      Object.keys(data.dignities).forEach(function (name) {
        var d = data.dignities[name] && data.dignities[name].dignity_es;
        if (d) digOf[norm(name)] = norm(d);
      });
    } catch (e) { return null; }
    for (var i = 0; i < DIG_PLANETS.length; i++) {
      var pl = DIG_PLANETS[i];
      var cls = digOf[pl];
      if (cls && DIG_CLASSES.indexOf(cls) > -1 && signOf[pl]) {
        return { planet: pl, cls: cls, sign: signOf[pl], key: pl + '_' + cls };
      }
    }
    return null;
  }

  // Relational-beat facts (spec 2026-07-29). Returns null when any lookup fails —
  // the caller must then leave the beat hidden (tested suppression rule).
  function relFacts(data) {
    if (!data) return null;
    var houses = data.houses || [], planets = data.planets || [];
    var h7 = null, venus = null, i;
    for (i = 0; i < houses.length; i++) { if (houses[i] && houses[i].house === 7) { h7 = houses[i]; break; } }
    for (i = 0; i < planets.length; i++) { if (planets[i] && norm(planets[i].name_es) === 'venus') { venus = planets[i]; break; } }
    if (!h7 || !h7.sign_es || !venus || !venus.sign_es || !(venus.house >= 1 && venus.house <= 12)) return null;
    return { casa7Sign: h7.sign_es, venusSign: venus.sign_es, venusHouse: venus.house };
  }

  // ── «Comprueba tu carta» desde la guía (plan 16 §M5.5, spec §3.A) ──────────
  // La guía enlaza aquí con #g=p.guia (esquema clave.valor unido por ~). Los datos de
  // nacimiento NUNCA van en la URL: se leen del almacén de la guía (aw_guia_v2, cifrado,
  // mismo origen) y solo con hora exacta, porque esta calculadora no tiene modo sin hora.
  // Sin el fragmento, la calculadora no mira el almacén.
  function guiaDoor(hash) {
    var m = /(?:^#?|&)g=([^&]*)/.exec(String(hash || ''));
    if (!m) return false;
    var v;
    try { v = decodeURIComponent(m[1]); } catch (e) { return false; }
    return v.split('~').indexOf('p.guia') > -1;
  }
  // Del almacén de la guía a la forma de AWPrefill.fill. null si falta algo o la hora no es exacta.
  function guiaPrefill(hash, data) {
    if (!guiaDoor(hash) || !data || !data.birth) return null;
    var b = data.birth;
    if (b.time_state !== 'exact') return null;
    var d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(b.fecha || ''));
    var h = /^(\d{1,2}):(\d{2})$/.exec(String(b.hora || ''));
    if (!d || !h) return null;
    var y = +d[1], mo = +d[2], dd = +d[3], hh = +h[1], mi = +h[2];
    if (y < 1900 || mo < 1 || mo > 12 || dd < 1 || dd > 31 || hh > 23 || mi > 59) return null;
    var city = String(b.ciudad || '').replace(/[<>]/g, '').trim();
    if (city.length < 2) return null;
    var pais = String(b.pais || '').replace(/[<>]/g, '').trim();
    var lat = b.lat == null || b.lat === '' ? NaN : Number(b.lat);
    var lon = b.lon == null || b.lon === '' ? NaN : Number(b.lon);
    var coords = isFinite(lat) && isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180;
    return {
      date: { y: y, m: mo, d: dd },
      time: { h: hh, mi: mi },
      place: pais ? city + ', ' + pais : city,
      lat: coords ? lat : null,
      lon: coords ? lon : null,
      name: null,
      emOk: !!data.em_ok
    };
  }

  // ── «Abrir en Chrome» desde la tarjeta para compartir (24-sep) ────────
  // En el navegador de Instagram o Facebook en Android no se puede compartir una imagen.
  // aw-share.js reabre el resultado en Chrome con su relleno tras el # (nunca llega al
  // servidor) y compartir=<diseño>: la puerta de email no se repite y la hoja se abre sola.
  var SHARE_DESIGNS = ['luna', 'instrumento', 'pliego'];
  function shareEscapeDesign(hash) {
    var m = /(?:^#?|&)compartir=([a-z]+)(?:&|$)/.exec(String(hash || ''));
    return m && SHARE_DESIGNS.indexOf(m[1]) > -1 ? m[1] : '';
  }
  function withoutShareKey(hash) {
    return String(hash || '').replace(/^#/, '').split('&').filter(function (kv) {
      return kv && kv.split('=')[0] !== 'compartir';
    }).join('&');
  }
  // Tras leerla, la marca sale de la barra (aw-prefill ya borró los datos de nacimiento).
  function dropShareKey() {
    try {
      var rest = withoutShareKey(window.location.hash);
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search + (rest ? '#' + rest : ''));
    } catch (e) {}
  }

  function track(name) {
    // Analytics hook — Phase 6 formalises the dataLayer wiring.
    // Intentionally carries NO birth data.
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name });
    } catch (e) {}
    try { document.dispatchEvent(new CustomEvent('aw:track', { detail: { name: name } })); } catch (e) {}
  }

  function initCalc(root) {
    var ORDER = ['intro', 'date', 'time', 'place', 'calc', 'gate', 'result'];
    var STEPNUM = { date: 1, time: 2, place: 3 };
    var current = 'intro';

    var apiUrl = root.getAttribute('data-api-url');
    var placesProxy = (root.getAttribute('data-places-proxy') || '').replace(/\/+$/, '');
    // sesión de Google y memoria de respuestas (aw-places.js, 2026-10-01)
    var places = (typeof AWPlaces !== 'undefined' && placesProxy) ? AWPlaces.client(placesProxy) : null;
    var lang = root.getAttribute('data-lang') || 'ES';

    var t = {};
    try { t = JSON.parse(root.querySelector('[data-aw-calc-i18n]').textContent); } catch (e) { t = {}; }

    var screens = {};
    Array.prototype.forEach.call(root.querySelectorAll('[data-aw-calc-screen]'), function (el) {
      screens[el.getAttribute('data-aw-calc-screen')] = el;
    });
    var chrome = root.querySelector('[data-aw-calc-chrome]');
    var stepCount = root.querySelector('[data-aw-calc-stepcount]');
    var progress = root.querySelector('[data-aw-calc-progress]');
    var progressFill = root.querySelector('[data-aw-calc-progress-fill]');
    var errorBox = root.querySelector('[data-aw-calc-error]');
    var submitBtn = root.querySelector('[data-aw-calc-submit]');

    var dayEl = root.querySelector('[data-aw-calc-day]');
    var monthEl = root.querySelector('[data-aw-calc-month]');
    var yearEl = root.querySelector('[data-aw-calc-year]');
    var hourEl = root.querySelector('[data-aw-calc-hour]');
    var minEl = root.querySelector('[data-aw-calc-min]');
    var placeEl = root.querySelector('[data-aw-calc-place]');
    var suggestEl = root.querySelector('[data-aw-calc-suggest]');

    // ── Places autocomplete state (lat/lon via the Worker proxy; NO API key here) ──
    var placeLat = null, placeLon = null;  // set when a chosen suggestion's details load
    var placePicked = false;               // true once a suggestion is chosen (sync gate)
    var acSeen = false;                    // proxy returned suggestions at least once
    var acFailed = false;                  // last proxy call errored (offline / 403 / CORS)
    var acItems = [];
    var acTimer = null;

    var booted = false;
    var started = false;
    function go(s, opts) {
      current = s;
      if (!started && STEPNUM[s]) { started = true; track('calculator_start'); } // first real step
      clearSuggest();
      ORDER.forEach(function (k) { if (screens[k]) screens[k].classList.toggle('is-active', k === s); });
      // La capa de respuestas (SEO) se apaga desde el teatro en adelante. Marca de estado
      // en la raíz, para que el CSS decida sin que el JS sepa nada del bloque hermano.
      root.setAttribute('data-screen', s);
      if (chrome) chrome.classList.toggle('can-back', s !== 'intro' && s !== 'result' && s !== 'calc' && s !== 'gate');
      if (STEPNUM[s]) {
        stepCount.textContent = fmt(T('paso {n} de 3'), { n: STEPNUM[s] });
        progress.classList.add('show');
        progressFill.style.width = (STEPNUM[s] / 3 * 100) + '%';
      } else if (s === 'calc') {
        // Teatro: el formulario está hecho, el cálculo es el último tramo. Raíl lleno.
        stepCount.textContent = '';
        progress.classList.add('show');
        progressFill.style.width = '100%';
      } else {
        stepCount.textContent = '';
        progress.classList.remove('show');
      }
      if (screens[s]) screens[s].scrollTop = 0;
      // Skip the scroll on initial boot (no jump on page load); on every step
      // change, bring the section just below the sticky header so the heading
      // is visible without scrolling up (scroll-margin-top does the offset).
      if (booted) { try { root.scrollIntoView({ block: 'start', behavior: 'smooth' }); } catch (e) {} }
      booted = true;
      if (hist) hist.at(s, !!(opts && opts.fromPop));
    }

    function back() {
      if (hist && hist.canBack()) { hist.back(); return; }
      var i = ORDER.indexOf(current);
      if (i > 0) go(ORDER[i - 1]);
    }

    // Historial por pantalla (14-oct, aw-step-history.js): «atrás» del móvil vuelve al paso anterior en vez
    // de cerrar el navegador de Instagram. Salir del teatro o de la puerta descarta el resultado en vuelo (live).
    var calcSeq = 0, calcLeft = false, runTh = null, runCtrl = null;
    function live(id) { return id === calcSeq && !calcLeft; }
    // Salir del teatro o de la puerta cancela el cálculo en curso: teatro parado, petición abortada, botón libre.
    function cancelRun() {
      calcLeft = true;
      if (runTh) { try { runTh.clear(); } catch (e) {} runTh = null; }
      if (runCtrl) { try { runCtrl.abort(); } catch (e) {} runCtrl = null; }
      setBusy(false);
    }
    var hist = window.AWStepHistory ? window.AWStepHistory.attach({ key: 'awCalc', transient: ['calc', 'gate'], landing: 'intro', onPop: function (s) {
      if (s === current) return;
      if (current === 'calc' || current === 'gate') cancelRun();
      go(s, { fromPop: true });
    } }) : null;

    function showError(msg) { if (errorBox) { errorBox.textContent = msg; errorBox.classList.add('is-show'); } }
    function clearError() { if (errorBox) { errorBox.textContent = ''; errorBox.classList.remove('is-show'); } }
    function setBusy(b) {
      if (!submitBtn) return;
      submitBtn.classList.toggle('is-busy', b);
      submitBtn.textContent = b ? (t.busyLabel || T('Calculando…')) : (submitBtn.getAttribute('data-label') || submitBtn.textContent);
    }

    // ── email gate (spec 2026-09-08) ─────────────────────────
    // Solo activo cuando el root lleva data-aw-gate (bandera gate_enabled de la
    // sección). Sin la bandera, calculate() se comporta exactamente como antes.
    var gateFlow = root.getAttribute('data-aw-gate');
    var guiaEmOk = false; // solo true con el relleno desde el almacén de la guía y em_ok válido
    var gateI18n = {};
    try { gateI18n = JSON.parse(root.querySelector('[data-aw-gate-i18n]').textContent); } catch (e) { gateI18n = {}; }
    function gateOn() { return !!(gateFlow && screens.gate && window.AWGate); }
    function theaterOn() { return !!(gateFlow && screens.calc && window.AWTheater); }
    // Propiedades permitidas hacia Klaviyo: signos y casas, nunca fecha, hora ni lugar.
    // Serie L3 (18-sep): más los signos de Mercurio a Saturno, las casas del Sol y la Luna
    // y lo que el teaser pintó con sus titulares aprobados (AWTeaser.lastGateFacts).
    var SIGN_PROP = { sol: 'calc_sol', luna: 'calc_luna', mercurio: 'calc_mercurio', venus: 'calc_venus', marte: 'calc_marte', jupiter: 'calc_jupiter', saturno: 'calc_saturno' };
    // A house number as this language writes it in a Klaviyo property: «7» in Spanish (aw-glosa.js),
    // «7th» in English (aw-glosa-en.js), so an English email can say «your 7th house» (plan inglés 4.x, 2026-10-21).
    function houseProp(n) { var g = typeof window !== 'undefined' && window.AWGlosa; return g && typeof g.ord === 'function' ? g.ord(n) : String(n); }
    function natalProps(data) {
      var out = {};
      var planets = (data && data.planets) || [];
      var angles = (data && data.angles) || [];
      planets.forEach(function (p) {
        var n = norm(p.name_es);
        if (SIGN_PROP[n]) out[SIGN_PROP[n]] = p.sign_es ? T(p.sign_es) : '';
        if ((n === 'sol' || n === 'luna') && p.house) out['calc_' + n + '_casa'] = houseProp(p.house);
      });
      angles.forEach(function (a) {
        if (norm(a.name_es) === 'ascendente') out.calc_asc = a.sign_es ? T(a.sign_es) : '';
        if (norm(a.name_es) === 'medio cielo') out.calc_mc = a.sign_es ? T(a.sign_es) : '';
      });
      // Which calculator page the lead came from (2026-10-29): the midheaven page's leads, measurable apart.
      if (focus) out.calc_focus = focus;
      var facts = window.AWTeaser && window.AWTeaser.lastGateFacts;
      if (facts) Object.keys(facts).forEach(function (k) { out[k] = facts[k]; });
      return out;
    }
    function showConfirmLine() {
      var el = root.querySelector('[data-aw-gate-confirm]');
      if (el) el.hidden = false;
    }
    function storageOrNull() { try { return window.localStorage; } catch (e) { return null; } }

    // ── validation ────────────────────────────────────────────
    function validDate() {
      var d = parseInt(dayEl.value, 10), y = parseInt(yearEl.value, 10);
      return d >= 1 && d <= 31 && y >= 1900 && y <= (new Date().getFullYear() + 1);
    }
    function validTime() {
      var h = parseInt(hourEl.value, 10), m = parseInt(minEl.value, 10);
      return h >= 0 && h <= 23 && m >= 0 && m <= 59;
    }
    function validPlace() { return placeEl && placeEl.value.trim().length >= 2; }

    function humanDate() {
      var monthName = monthEl.options[monthEl.selectedIndex].text;
      if (isEn()) return monthName + ' ' + parseInt(dayEl.value, 10) + ', ' + yearEl.value; // US: March 14, 2027
      if (isFr()) return dateFr(dayEl.value, monthName, yearEl.value); // 14 mars 2027
      return dayEl.value + ' de ' + monthName + ' de ' + yearEl.value;
    }

    // ── Places autocomplete (via Cloudflare Worker proxy) ──────
    // The Google key stays server-side in the proxy. We only ever call the public
    // proxy; selecting a suggestion fetches lat/lon so the API skips geocoding.
    function clearSuggest() {
      acItems = [];
      if (suggestEl) { suggestEl.innerHTML = ''; suggestEl.hidden = true; }
      if (placeEl) placeEl.setAttribute('aria-expanded', 'false');
    }
    function renderSuggest(items) {
      acItems = items || [];
      if (!suggestEl) return;
      if (!acItems.length) { clearSuggest(); return; }
      suggestEl.innerHTML = acItems.map(function (s, i) {
        return '<li class="aw-calc__suggest-item" role="option" data-i="' + i + '">' +
          '<button type="button" class="aw-calc__suggest-btn" tabindex="-1">' +
          '<span class="aw-calc__c-name">' + esc(s.name) + '</span>' +
          (s.region ? '<span class="aw-calc__c-region">' + esc(s.region) + '</span>' : '') +
          '</button></li>';
      }).join('');
      suggestEl.hidden = false;
      if (placeEl) placeEl.setAttribute('aria-expanded', 'true');
    }
    function fetchAutocomplete(q) {
      if (!places) return;
      places.suggest(q)
        .then(function (d) {
          acFailed = false;
          var arr = (d && d.predictions) || [];
          var items = arr.slice(0, 5).map(function (p) {
            return {
              text: p.description || p.main_text || '',
              name: p.main_text || p.description || '',
              region: p.secondary_text || '',
              id: p.place_id || ''
            };
          }).filter(function (x) { return x.text; });
          if (items.length) acSeen = true;
          if (placeEl.value.trim().length >= 3) renderSuggest(items); // ignore stale
        })
        .catch(function () { acFailed = true; renderSuggest([]); });
    }
    function pickSuggestion(item) {
      if (!item) return;
      placeEl.value = item.text;
      placePicked = true;
      clearSuggest();
      clearError();
      placeLat = null; placeLon = null;
      if (!item.id || !places) return;
      places.details(item.id)
        .then(function (d) {
          if (d && d.lat != null && d.lng != null) { placeLat = Number(d.lat); placeLon = Number(d.lng); }
        })
        .catch(function () { placeLat = null; placeLon = null; });
    }

    // ── the call ──────────────────────────────────────────────
    function calculate() {
      clearError();
      if (!validDate() || !validTime() || !validPlace()) { showError(t.errInvalid || T('Revisa los datos.')); return; }
      // Require choosing a suggestion (so lat/lon are sent) whenever autocomplete works.
      if (!placePicked && acSeen && !acFailed) { showError(t.placePick || T('Elige tu ciudad de la lista.')); return; }

      var payload = {
        date: yearEl.value + '-' + pad2(parseInt(monthEl.value, 10)) + '-' + pad2(parseInt(dayEl.value, 10)),
        time: pad2(parseInt(hourEl.value, 10)) + ':' + pad2(parseInt(minEl.value, 10)),
        place: placeEl.value.trim(),
        lang: lang
      };
      if (placeLat != null && placeLon != null) { payload.lat = placeLat; payload.lon = placeLon; }
      lastPayload = payload;
      var placeText = payload.place;
      var dateText = humanDate();
      var timeText = isEn() ? time12(payload.time) : payload.time;
      if (isFr()) timeText = timeFr(payload.time); // 14 h 30

      var reqId = ++calcSeq; calcLeft = false;
      setBusy(true);
      var th = null;
      var floorP = Promise.resolve();
      if (theaterOn()) {
        go('calc');
        track('calculator_theater_start');
        th = window.AWTheater.start(screens.calc, { lines: gateI18n.theater || [], floor: window.AWTheater.GATE_FLOOR, spread: true, slowText: gateI18n.slowText || '' });
        runTh = th;
        floorP = th.floorP;
      }
      runCtrl = null;
      try { runCtrl = new AbortController(); } catch (e) {}
      fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        mode: 'cors',
        credentials: 'omit',
        signal: runCtrl ? runCtrl.signal : undefined
      }).then(function (res) {
        if (!res.ok) { var err = new Error('http'); err.status = res.status; throw err; }
        return res.json();
      }).then(function (data) {
        if (!live(reqId)) return; // cancelado o superado por otra petición: no se pinta nada
        renderResult(data, placeText, dateText, timeText);
        try { fillRelBeat(data); } catch (e) {}
        try { fillDigBeat(data); } catch (e) {}
        // Draw the real wheel from the response (decorative wheel stays if absent/fails).
        try { if (window.AWWheel && window.AWWheel.draw) window.AWWheel.draw(data, root); } catch (e) {}
        // «Comparte tu carta» (24-sep): la tira bajo la rueda y su hoja. Llamada directa,
        // como el teaser. lastPayload solo sirve para el cumpleaños (día y mes), el
        // hemisferio de la Luna y «Abrir en Chrome», que nunca se comparte.
        try { if (window.AWShare && window.AWShare.natal) shareP = window.AWShare.natal(root, data, lastPayload, track); } catch (e) {}
        // Hand the response to the optional teaser module via direct call.
        // Deliberately NOT a document-level event: third-party app scripts on
        // the storefront could passively listen to a broadcast and read the
        // full response (it echoes birth date/time in calculation_meta).
        // Not persisted or logged by this file.
        var teaserDone = null;
        try { if (window.AWTeaser && window.AWTeaser.onResult) teaserDone = window.AWTeaser.onResult(data); } catch (e) {}
        // Render-before-switch (spec 2026-07-29): wait up to 900 ms for the teaser
        // so the result appears complete instead of shifting mid-read.
        var teaserP = new Promise(function (resolve) {
          if (teaserDone && typeof teaserDone.then === 'function') {
            teaserDone.then(resolve, resolve);
            setTimeout(resolve, 900);
          } else { resolve(); }
        });
        // Gate (spec 2026-09-08): el resultado ya está renderizado en su pantalla
        // oculta; la puerta aparece en max(suelo del teatro, teaser) y el reveal
        // es exactamente el de antes.
        return Promise.all([teaserP, floorP]).then(function () { return data; });
      }).then(function (data) {
        if (th) th.clear();
        if (!live(reqId)) { if (reqId === calcSeq) setBusy(false); return; }
        var showResult = function () {
          if (!live(reqId)) return;
          setBusy(false);
          go('result');
          track('calculator_complete'); // no birth data
          if (shareEscape && shareP && shareP.then) {
            shareP.then(function (ctl) { if (ctl) ctl.open(shareEscape); }).catch(function () {});
          }
        };
        // Desde la guía con EM ya pasado en este dispositivo (em_ok con el mismo nacimiento): sin gate.
        // Desde «Abrir en Chrome» (shareEscape), tampoco: ya vio su resultado en el navegador de la app.
        if (!gateOn() || guiaEmOk || !window.AWGate.shouldShow({ storage: storageOrNull(), search: window.location.search, known: root.hasAttribute('data-aw-gate-known') }) || shareEscape) {
          showResult();
          return;
        }
        go('gate');
        // verifyUrl (18-sep): la API verifica el buzón y elige la vía; la línea de
        // confirmación la pone showRoute cuando llega su respuesta, tras el reveal.
        var gateCfg = {
          flow: 'natal', hard: true,
          listId: root.getAttribute('data-aw-gate-list') || '',
          companyId: root.getAttribute('data-aw-gate-company') || '',
          verifyUrl: root.getAttribute('data-aw-gate-verify') || '',
          // Función, no objeto: se lee al enviar, cuando el teaser ya dejó sus datos.
          props: function () { return natalProps(data); },
          storage: storageOrNull(),
          track: track,
          onLead: window.awSendLead,
          // Campaña de leads (plan 2026-09-25): buzón verificado y 25 a 64 años → evento
          // LeadCualificadoCartaNatal al Worker CAPI. La fecha solo decide si se manda, nunca sale.
          onRoute: function (route, r) {
            try { if (window.AWAttrib && window.AWAttrib.sendQualified) window.AWAttrib.sendQualified({ route: route, email: r && r.email, birthDate: lastPayload && lastPayload.date }); } catch (e) {}
          },
          errInvalid: gateI18n.errInvalid
        };
        return window.AWGate.run(screens.gate, gateCfg).then(function (out) {
          if (!live(reqId)) return;
          if (out && out.captured) {
            if (out.route && typeof window.AWGate.showRoute === 'function') window.AWGate.showRoute(root, out.route, gateCfg);
            else showConfirmLine();
          }
          showResult();
        });
      }).catch(function (err) {
        if (th) th.clear();
        if (!live(reqId)) { if (reqId === calcSeq) setBusy(false); return; }
        setBusy(false);
        if (current === 'calc' || current === 'gate') go('place');
        showError(errorMessage(err && err.status));
      });
    }

    function errorMessage(status) {
      if (status === 422) return t.err422 || t.errInvalid;
      if (status === 400) return t.err400;
      if (status === 429) return t.err429;
      if (status === 502) return t.err502;
      return t.errNetwork;
    }

    // ── render the live response ──────────────────────────────
    // gloss (2026-10-12): what that planet or point and its house are, one muted
    // line from aw-glosa.js. Empty without the helper.
    function row(glyph, title, casa, retro, gloss) {
      return '<div class="aw-calc__pos-row">' +
        '<span class="aw-calc__pos-glyph">' + forceText(esc(glyph)) + '</span>' +
        '<div class="aw-calc__pos-body"><h3>' + esc(title) + '</h3>' +
        (casa ? '<p>' + esc(casa) + '</p>' : '') +
        (gloss ? '<p class="aw-calc__pos-gloss">' + esc(gloss) + '</p>' : '') + '</div>' +
        (retro ? '<span class="aw-calc__r-chip" title="' + esc(T('retrógrado')) + '">R</span>' : '') +
        '</div>';
    }
    function deg(o) { return o && o.degree ? o.degree.formatted : ''; }
    function glosa() { return window.AWGlosa || null; }
    // {casa_glosa} in a template: the house's plain line, or the clause drops out.
    function fmtCasa(tpl, map, n) {
      var G = glosa(), cg = G ? G.casaDe(n) : '';
      var out = String(tpl || '');
      if (!cg) out = out.replace(/, \{casa_glosa\}/g, '');
      map.casa_glosa = cg;
      return fmt(out, map);
    }
    function rowGloss(name, house) { var G = glosa(); return G ? G.fila(name, house) : ''; }
    function planetTitle(p) { return T(p.name_es) + ' · ' + T(p.sign_es) + ' ' + deg(p); }

    // ── foco del resultado (páginas calcular-ascendente y calcular-signo-lunar) ──
    // La misma calculadora, pero el resultado abre con la pieza que la persona vino
    // a buscar. El resto de la carta sigue debajo, igual que en la calculadora natal.
    var focus = root.getAttribute('data-aw-calc-focus') || '';
    var lastPayload = null;
    var shareP = null; // promesa del controlador de «Comparte tu carta» (aw-share.js)
    function focusCard(data, placeText) {
      var hit = null, key = '', note = '';
      if (focus === 'ascendente') {
        (data.angles || []).forEach(function (a) { if (norm(a.name_es) === 'ascendente') hit = a; });
        key = t.focusAscKey || T('Tu ascendente');
        note = fmt(t.focusAscNote || T('Es la primera impresión que dejas, cómo te ven antes de conocerte. Lo marca el signo que asomaba por el horizonte de {place} cuando naciste, así que depende de la hora exacta: si la tuya es aproximada, tómalo como provisional.'), { place: placeText });
      } else if (focus === 'luna') {
        (data.planets || []).forEach(function (p) { if (norm(p.name_es) === 'luna') hit = p; });
        key = t.focusMoonKey || T('Tu signo lunar');
        if (hit) {
          note = fmtCasa(t.focusMoonNote || T('Es lo que necesitas para sentirte a salvo y lo que te devuelve la calma. Tu Luna está en {sign}. Con la hora que has puesto cae en tu casa {n}, {casa_glosa}: el área de tu vida donde más buscas ese refugio. La casa solo vale si esa hora es exacta.'), { sign: T(hit.sign_es), n: hit.house }, hit.house);
          // The Moon moves about 13° a day: at 0° or 29° an approximate hour can flip the sign too (astrologer, 2026-10-12).
          var md = hit.degree && typeof hit.degree.deg === 'number' ? hit.degree.deg : -1;
          if (md === 0 || md === 29) note += ' ' + (t.focusMoonEdge || T('Tu Luna está muy cerca del cambio de signo, así que si la hora es aproximada el signo también puede variar.'));
        }
      } else if (focus === 'mediocielo') {
        // Midheaven page (English only, 2026-10-29): the card opens with what that sign means for her, then what the
        // Midheaven is. Its texts come from the section settings, not the dictionary.
        (data.angles || []).forEach(function (a) { if (norm(a.name_es) === 'medio cielo') hit = a; });
        key = t.focusMcKey || T('Medio Cielo');
        if (hit) {
          var line = signLine(t.focusMcSigns, [hit.sign_es, T(hit.sign_es)]);
          note = [line, fmt(t.focusMcNote || '', { place: placeText })].filter(Boolean).join(' ');
          // About one degree every four minutes: within two degrees of a sign change, a rounded birth time can flip it.
          var cd = hit.degree && typeof hit.degree.deg === 'number' ? hit.degree.deg : -1;
          if (t.focusMcEdge && cd >= 0 && (cd <= 1 || cd >= 28)) note += ' ' + t.focusMcEdge;
        }
      }
      if (!hit) return '';
      var g = SIGN_GLYPHS[norm(hit.sign_es)] || '';
      return '<div class="aw-calc__focus">' +
        '<p class="aw-calc__focus-k">' + esc(key) + '</p>' +
        '<p class="aw-calc__focus-v"><span aria-hidden="true">' + forceText(esc(g)) + '</span> ' + esc(T(hit.sign_es)) + ' <small>' + esc(deg(hit)) + '</small></p>' +
        '<p class="aw-calc__focus-n">' + esc(note) + '</p>' +
        (focus === 'luna' ? '<p class="aw-calc__focus-n" data-aw-calc-moon-day></p>' : '') +
        '</div>';
    }
    // Signo lunar sin hora exacta: la Luna pasa algo más de dos días en cada signo
    // (media 2,28), así que algo más de la mitad de los días cae entero en el mismo. Se mira a las 00:00 y a las
    // 23:59 del mismo día y lugar, y se dice la verdad en los dos casos.
    function moonDayCheck(moonSign) {
      var el = root.querySelector('[data-aw-calc-moon-day]');
      if (!el || !lastPayload || !apiUrl) return;
      function at(time) {
        var b = { date: lastPayload.date, time: time, place: lastPayload.place, lang: lastPayload.lang };
        if (lastPayload.lat != null && lastPayload.lon != null) { b.lat = lastPayload.lat; b.lon = lastPayload.lon; }
        return fetch(apiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(b), mode: 'cors', credentials: 'omit' })
          .then(function (r) { if (!r.ok) throw new Error('http'); return r.json(); })
          .then(function (d) { var s = ''; (d.planets || []).forEach(function (p) { if (norm(p.name_es) === 'luna') s = p.sign_es; }); return s; });
      }
      Promise.all([at('00:00'), at('23:59')]).then(function (r) {
        if (!r[0] || !r[1]) return;
        el.textContent = r[0] === r[1]
          ? fmt(t.focusMoonSame || T('Ese día la Luna estuvo en {sign} las 24 horas, así que tu signo lunar es {sign} aunque tu hora de nacimiento no sea exacta.'), { sign: T(r[0]) })
          : fmt(t.focusMoonChange || T('Ese día la Luna cambió de signo: empezó en {a} y terminó en {b}. Con la hora que has puesto sale {sign}. Si tu hora no es segura, este es el dato que conviene confirmar.'), { a: T(r[0]), b: T(r[1]), sign: T(moonSign) });
      }).catch(function () {});
    }

    function renderResult(data, placeText, dateText, timeText) {
      var planets = data.planets || [];
      var angles = data.angles || [];

      root.querySelector('[data-aw-calc-result-lead]').textContent =
        fmt(t.resultLeadTpl, { place: placeText, date: dateText, time: timeText });

      // Las 8 fases de Rudhyar (phase_es, 24-sep), las mismas que la tarjeta para compartir.
      // name_es queda para quien aún lo lea (la guía).
      var moonEl = root.querySelector('[data-aw-calc-moon]');
      var phaseName = data.lunar_phase && (data.lunar_phase.phase_es || data.lunar_phase.name_es);
      if (phaseName) {
        var moonPre = forceText(t.moonPrefix || '');
        moonEl.textContent = moonPre + (moonPre && !/\s$/.test(moonPre) ? ' ' : '') + T(phaseName);
        moonEl.hidden = false;
      } else { moonEl.hidden = true; }

      var personal = [], social = [], pointPlanets = [];
      planets.forEach(function (p) {
        var b = bucket(p.name_es);
        if (b === 'personal') personal.push(p);
        else if (b === 'social') social.push(p);
        else pointPlanets.push(p);
      });
      pointPlanets.sort(function (a, b) { return pointRank(a.name_es) - pointRank(b.name_es); });

      var casaTpl = t.casaTpl || 'Casa {n}';
      // La tarjeta del foco queda a la vista, en el ancla de «Tu carta». Los tres grupos
      // van al acordeón de los planetas, cerrado (founder, 25-sep). Sin ese acordeón en el
      // marcado (sección vieja, o rechazada por el sync), se pintan en el ancla como antes.
      var focusHtml = focus ? focusCard(data, placeText) : '';
      var html = '';
      if (personal.length) {
        html += '<h2 class="aw-calc__group-title">' + esc(t.groupPersonal) + '</h2>';
        personal.forEach(function (p) { html += row(p.glyph, planetTitle(p), fmt(casaTpl, { n: p.house }), p.retrograde, rowGloss(p.name_es, p.house)); });
      }
      if (social.length) {
        html += '<h2 class="aw-calc__group-title">' + esc(t.groupSocial) + '</h2>';
        social.forEach(function (p) { html += row(p.glyph, planetTitle(p), fmt(casaTpl, { n: p.house }), p.retrograde, rowGloss(p.name_es, p.house)); });
      }
      // Points & angles: Ascendente + Descendente (one axis, kept adjacent) then
      // Medio Cielo, then nodes/Quirón/Lilith. The Descendente row is the
      // relational seed in the proof zone (spec 2026-07-29): pure data, cusp of
      // casa 7 by construction.
      var ANGLE_RANK = { 'ascendente': 1, 'descendente': 2, 'medio cielo': 3 };
      var anglePts = angles.filter(function (a) { return ANGLE_RANK[norm(a.name_es)] != null; });
      anglePts.sort(function (a, b) { return ANGLE_RANK[norm(a.name_es)] - ANGLE_RANK[norm(b.name_es)]; });
      if (anglePts.length || pointPlanets.length) {
        html += '<h2 class="aw-calc__group-title">' + esc(t.groupPoints) + '</h2>';
        anglePts.forEach(function (a) {
          var g = SIGN_GLYPHS[norm(a.sign_es)] || '';
          html += row(g, T(a.name_es) + ' · ' + T(a.sign_es) + ' ' + deg(a), '', false, rowGloss(a.name_es));
        });
        pointPlanets.forEach(function (p) { html += row(p.glyph, planetTitle(p), '', false, rowGloss(p.name_es)); });
      }
      var pWrap = root.querySelector('[data-aw-calc-planets-wrap]');
      var pBody = root.querySelector('[data-aw-calc-planets]');
      if (pWrap && pBody) {
        pBody.innerHTML = html;
        pWrap.hidden = !html;
        root.querySelector('[data-aw-calc-positions]').innerHTML = focusHtml;
      } else {
        root.querySelector('[data-aw-calc-positions]').innerHTML = focusHtml + html;
      }
      if (focus === 'luna') {
        var moonP = null;
        planets.forEach(function (p) { if (norm(p.name_es) === 'luna') moonP = p; });
        if (moonP) moonDayCheck(moonP.sign_es);
      }

      // houses
      var houses = data.houses || [];
      var hWrap = root.querySelector('[data-aw-calc-houses-wrap]');
      if (houses.length) {
        var G0 = glosa();
        var hl = houses.map(function (h) {
          var hg = G0 ? G0.casaCorta(h.house) : '';
          return '<p class="aw-calc__acc-line">' +
            esc(fmt(t.housesLineTpl, { n: h.house, sign: T(h.sign_es), deg: deg(h) })) +
            (hg ? '<span class="aw-calc__acc-gloss">' + esc(hg.charAt(0).toUpperCase() + hg.slice(1)) + '</span>' : '') + '</p>';
        }).join('');
        root.querySelector('[data-aw-calc-houses]').innerHTML = hl;
        hWrap.hidden = false;
      } else { hWrap.hidden = true; }

      // aspects tease
      var aspects = data.aspects || [];
      var aWrap = root.querySelector('[data-aw-calc-aspect-wrap]');
      if (aspects.length) {
        root.querySelector('[data-aw-calc-aspect-count]').textContent = fmt(t.aspectsCountTpl, { n: aspects.length });
        var shown = rankAspectsRelational(aspects).slice(0, 3);
        var chips = shown.map(function (a) {
          return '<span>' + esc(fmt(T('{type} {p1}-{p2}'), { type: T(a.type_es), p1: T(a.p1_es), p2: T(a.p2_es) })) + '</span>';
        }).join('');
        root.querySelector('[data-aw-calc-aspect-chips]').innerHTML = chips;
        // One muted line: what each aspect type shown is (aw-glosa.js), once each.
        var agEl = root.querySelector('[data-aw-calc-aspect-gloss]');
        if (agEl) {
          var G1 = glosa();
          agEl.textContent = G1 ? G1.tiposAspecto(shown.map(function (a) { return a.type_es; })) : '';
          agEl.hidden = !agEl.textContent;
        }
        var closeEl = root.querySelector('[data-aw-calc-aspect-close]');
        if (closeEl) {
          closeEl.textContent = aspectCloseText(t, shown.length, aspects.length);
          closeEl.hidden = !closeEl.textContent;
        }
        aWrap.hidden = false;
      } else { aWrap.hidden = true; }
    }

    // ── dignity end-beat: un último dato (spec 2026-07-30) ────
    // Fills the beat from the astrologer-approved dignity library and unhides
    // it. Suppression rule (tested): no qualifying classical dignity, missing
    // markup, failed library fetch or missing entry all leave it hidden.
    var digLibPromise = null;
    var digSeenFired = false;
    function fillDigBeat(data) {
      var beat = root.querySelector('[data-aw-calc-digbeat]');
      if (!beat) return;
      var pickDig = selectDignity(data);
      if (!pickDig) { beat.hidden = true; return; }
      var libUrl = beat.getAttribute('data-aw-dig-lib');
      if (!libUrl) return;
      if (!digLibPromise) {
        digLibPromise = fetch(libUrl, { credentials: 'omit' })
          .then(function (r) { if (!r.ok) throw new Error('http'); return r.json(); })
          .catch(function () { return null; });
      }
      digLibPromise.then(function (lib) {
        var entry = lib && lib.entries && lib.entries[pickDig.key];
        var aEl = beat.querySelector('[data-aw-calc-dig-anchor]');
        var bEl = beat.querySelector('[data-aw-calc-dig-body]');
        var dEl = beat.querySelector('[data-aw-calc-dig-defer]');
        if (!entry || !entry.anchor || !entry.body || !aEl || !bEl || !dEl) { beat.hidden = true; return; }
        aEl.textContent = String(entry.anchor).replace(/\{signo\}/g, T(pickDig.sign));
        bEl.textContent = entry.body;
        dEl.textContent = entry.deferral || '';
        // Insight first (2026-10-12): the titular leads, the fact and its glosa follow the body.
        var tEl = beat.querySelector('[data-aw-calc-dig-titular]');
        if (tEl) { tEl.textContent = entry.titular || ''; tEl.hidden = !entry.titular; }
        var gEl = beat.querySelector('[data-aw-calc-dig-gloss]');
        if (gEl) {
          var Gd = glosa();
          gEl.textContent = Gd ? Gd.dignidad(pickDig.planet, pickDig.cls) : '';
          gEl.hidden = !gEl.textContent;
        }
        beat.hidden = false;
        if (digSeenFired) return;
        var fireSeen = function () { if (!digSeenFired) { digSeenFired = true; track('dignity_seen'); } }; // structure only, never which dignity
        if (typeof IntersectionObserver === 'function') {
          try {
            var io = new IntersectionObserver(function (entries2) {
              for (var i = 0; i < entries2.length; i++) {
                if (entries2[i].isIntersecting) { fireSeen(); io.disconnect(); break; }
              }
            }, { threshold: 0.4 });
            io.observe(beat);
          } catch (e) { fireSeen(); }
        } else { fireSeen(); }
      }).catch(function () { beat.hidden = true; });
    }

    // ── relational beat: la otra carta (spec 2026-07-29) ──────
    // Fills the personalized anchor (casa 7 cusp + Venus) and unhides the beat.
    // Suppression rule: any failed lookup or missing template leaves the block
    // hidden entirely. No-ops silently when the markup is absent (flag off, or
    // the section file was rejected by the sync while assets landed).
    var relSeenFired = false;
    function fillRelBeat(data) {
      var beat = root.querySelector('[data-aw-calc-relbeat]');
      if (!beat) return;
      var anchorEl = beat.querySelector('[data-aw-calc-rel-anchor]');
      var facts = relFacts(data);
      if (!facts || !anchorEl || !t.relCasa7Tpl || !t.relVenusTpl) { beat.hidden = true; return; }
      anchorEl.textContent =
        fmt(T(t.relCasa7Tpl), { sign: T(facts.casa7Sign) }) + ' ' +
        fmtCasa(T(t.relVenusTpl), { sign: T(facts.venusSign), n: facts.venusHouse }, facts.venusHouse);
      beat.hidden = false;
      // relational_seen: one-shot viewport event (guardrail denominator). Without
      // IntersectionObserver it fires on render — a documented over-count that
      // keeps the denominator alive on old WebViews. Structure only, no birth data.
      if (relSeenFired) return;
      var fireSeen = function () { if (!relSeenFired) { relSeenFired = true; track('relational_seen'); } };
      if (typeof IntersectionObserver === 'function') {
        try {
          var io = new IntersectionObserver(function (entries) {
            for (var i = 0; i < entries.length; i++) {
              if (entries[i].isIntersecting) { fireSeen(); io.disconnect(); break; }
            }
          }, { threshold: 0.4 });
          io.observe(beat);
        } catch (e) { fireSeen(); }
      } else { fireSeen(); }
    }

    // ── wiring ────────────────────────────────────────────────
    Array.prototype.forEach.call(root.querySelectorAll('[data-aw-calc-go]'), function (btn) {
      btn.addEventListener('click', function () {
        var target = btn.getAttribute('data-aw-calc-go');
        var v = btn.getAttribute('data-aw-calc-validate');
        if (v === 'date' && !validDate()) { return; }
        if (v === 'time' && !validTime()) { return; }
        go(target);
      });
    });
    var backBtn = root.querySelector('[data-aw-calc-back]');
    if (backBtn) backBtn.addEventListener('click', back);
    // Desplegable del bloque del Profundizador en el puente (2026-09-11): una señal la
    // primera vez que se abre. Solo estructura.
    var bridgeMore = root.querySelector('[data-aw-calc-bridge-more]');
    if (bridgeMore) {
      bridgeMore.addEventListener('toggle', function onMore() {
        if (!bridgeMore.open) return;
        bridgeMore.removeEventListener('toggle', onMore);
        track('calculator_bridge_more_open');
      });
    }

    // place autocomplete wiring
    if (placeEl && suggestEl) {
      placeEl.addEventListener('input', function () {
        placeLat = null; placeLon = null; placePicked = false; clearError(); // editing invalidates any prior pick
        var q = placeEl.value.trim();
        if (acTimer) clearTimeout(acTimer);
        if (q.length < 3) { clearSuggest(); return; }
        acTimer = setTimeout(function () { fetchAutocomplete(q); }, 350);
      });
      placeEl.addEventListener('blur', function () { setTimeout(clearSuggest, 150); });
      // mousedown (not click) so the pick lands before the input's blur fires
      suggestEl.addEventListener('mousedown', function (e) {
        var li = e.target.closest('[data-i]'); if (!li) return;
        e.preventDefault();
        pickSuggestion(acItems[parseInt(li.getAttribute('data-i'), 10)]);
      });
    }
    Array.prototype.forEach.call(root.querySelectorAll('[data-aw-calc-why]'), function (btn) {
      btn.addEventListener('click', function () {
        var box = root.querySelector('#' + btn.getAttribute('data-aw-calc-why'));
        if (box) box.classList.toggle('is-open');
      });
    });
    if (submitBtn) {
      submitBtn.setAttribute('data-label', submitBtn.textContent);
      submitBtn.addEventListener('click', calculate);
    }

    // Con relleno válido NO se pasa por la intro: aw-prefill-boot ya pintó el
    // teatro en el primer frame, y un go('intro') aquí lo haría parpadear (y el
    // go('calc') posterior haría scroll suave, porque ya no sería el arranque).
    // Desde la guía (#g=p.guia) el relleno llega del almacén, que se lee de forma asíncrona:
    // la intro espera a saber si hay una hora exacta que calcular. Un relleno por URL manda.
    // «Abrir en Chrome» (24-sep): la marca se lee y sale de la barra antes del relleno.
    var shareEscape = shareEscapeDesign(window.location.hash);
    if (shareEscape) dropShareKey();
    var guiaDoorOpen = !!window.AWGuiaStore && !!window.AWPrefill && guiaDoor(window.location.hash);
    var pre = window.AWPrefill ? window.AWPrefill.read(window.location.search, window.location.hash) : null;
    var prefilled = !!(pre && window.AWPrefill.fill(root, 'aw-calc', pre.a, null));
    if (!prefilled && !guiaDoorOpen) go('intro');

    // ── relleno desde la URL ────────────────────────────────────────────
    // Un asistente que ya conoce fecha, hora y lugar entrega un enlace y esto
    // arranca el calculo solo, en vez de mandar a rellenar tres pantallas.
    // Cae en la pantalla de CALCULO, no en el resultado, asi que la puerta de
    // email aparece igual que si lo hubiera tecleado una persona. AWPrefill
    // borra los parametros de la barra al leerlos: los datos de nacimiento no
    // se quedan en el historial ni viajan en el Referer.
    if (prefilled) {
      if (pre.a.lat != null && pre.a.lon != null) { placeLat = pre.a.lat; placeLon = pre.a.lon; placePicked = true; }
      window.AWPrefill.scrub();
      track('calculator_prefill');
      // Los scripts van con defer y este corre ANTES que aw-theater.js y aw-gate.js:
      // calcular ya mismo dejaba theaterOn() en falso y el relleno saltaba del
      // primer frame a la puerta en ~1 s, sin animación. Se espera al final del
      // parseo (DOMContentLoaded llega cuando ya corrieron todos los defer).
      var runPrefill = function () {
        if (runPrefill.done) return;
        runPrefill.done = true;
        calculate();
      };
      if (window.AWTheater || document.readyState === 'complete') runPrefill();
      else {
        document.addEventListener('DOMContentLoaded', runPrefill);
        window.addEventListener('load', runPrefill);
      }
    }

    // La marca solo vale con su propio relleno completo tras el #.
    if (!(prefilled && pre.desdeHash)) shareEscape = '';

    // ── relleno desde la guía (#g=p.guia) ───────────────────────────────
    // Mismo aterrizaje que el relleno por URL (pantalla de cálculo, nunca el resultado),
    // pero los datos salen del almacén cifrado de la guía, nunca de la URL. Sin almacén
    // legible, o sin hora exacta, la calculadora abre en la intro como siempre. El gate
    // se salta con em_ok válido, además de las vías de siempre (aw_gate_done, _kx, sesión).
    if (guiaDoorOpen && !prefilled) {
      // El listener se pone mientras corren los defer: salta cuando aw-theater.js y aw-gate.js ya están.
      var deferDone = new Promise(function (resolve) {
        if (document.readyState === 'complete') { resolve(); return; }
        document.addEventListener('DOMContentLoaded', resolve);
        window.addEventListener('load', resolve);
      });
      var guiaStore = null;
      var loadP;
      try { guiaStore = window.AWGuiaStore.create(); loadP = guiaStore.load(); } catch (e) { loadP = Promise.resolve(null); }
      Promise.all([loadP, deferDone]).then(function (r) {
        var g = guiaPrefill(window.location.hash, r[0]);
        if (!g || !window.AWPrefill.fill(root, 'aw-calc', g, null)) { go('intro'); return; }
        var emP = g.emOk && guiaStore ? guiaStore.emValid() : Promise.resolve(false);
        return emP.then(null, function () { return false; }).then(function (ok) {
          guiaEmOk = !!ok;
          if (g.lat != null && g.lon != null) { placeLat = g.lat; placeLon = g.lon; placePicked = true; }
          track('calculator_prefill_guia'); // solo estructura, nunca los datos
          calculate();
        });
      }).catch(function () { if (!booted) go('intro'); });
    }
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }
  // Pure helpers exported for the Node tests (same pattern as aw-teaser.js).
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { time12: time12, timeFr: timeFr, dateFr: dateFr, rankAspectsRelational: rankAspectsRelational, aspectCloseText: aspectCloseText, relFacts: relFacts, selectDignity: selectDignity, guiaDoor: guiaDoor, guiaPrefill: guiaPrefill, shareEscapeDesign: shareEscapeDesign, withoutShareKey: withoutShareKey, _norm: norm, signLine: signLine };
  }
  if (typeof document !== 'undefined') {
    ready(function () {
      Array.prototype.forEach.call(document.querySelectorAll('[data-aw-calc]'), initCalc);
    });
  }
})();
